/* eslint-disable @typescript-eslint/camelcase */
import { Status, NonClientError, ServerError } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useFmcHost from 'hooks/useFmcHost';

import useQueryParameters from 'hooks/useQueryParameters';

import { CreditApplication } from 'state/creditApplication';

import { InstantCredit, mergeInstantCredit } from 'state/instantCredit';
import { selectTenant } from 'state/tenant';
import useIovation from 'hooks/useIovation';
import {
  receivePrequalApplicationResult,
  PrequalApplicationResultType,
} from 'fmc-sdk';
import catchOnly from 'utils/catchOnly';
import useAnalyticsVariables from 'hooks/useAnalyticVariables';
import useHandleExistingAccount from './handleExistingAccount';
import { selectPrequalOffer } from 'state/prequalOffer';
import { transformDob } from 'utils/dateUtils';

const PATH = '/api/prequalification_acceptance';

type PrequalificationApplicationHook = () => [
  (params: CreditApplication, reCaptchaToken: string) => Promise<InstantCredit>,
  Status,
  boolean,
];

export class PrequalificationApplicationError extends Error {}

const usePrequalificationApplication: PrequalificationApplicationHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const {
    storeNumber: queryStoreNumber,
    token,
    mockPrequalApp,
    location,
    channel,
    subchannel,
    applicationSubType,
    departmentId,
  } = useQueryParameters();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();
  const {
    setAnalyticsVariables,
    setAnalyticsVariablesByApplication,
  } = useAnalyticsVariables();
  const handleExistingAccount = useHandleExistingAccount();
  const { prequalificationId } = useSelector(selectPrequalOffer);
  const { defaultStoreNumber } = useSelector(selectTenant);

  const iovationBlackbox = useIovation();

  const applyForCredit = useCallback(
    async (
      params: CreditApplication,
      reCaptchaToken: string,
    ): Promise<InstantCredit> => {
      setError(false);

      const blackbox = await iovationBlackbox;

      const overrides: Partial<CreditApplication> = {
        storeNumber:
          params.storeNumber || queryStoreNumber || defaultStoreNumber,
      };

      params = transformDob(['dateOfBirth'], params);
      if (params.authBuyer) {
        params = transformDob(['authBuyer', 'dateOfBirth'], params);
      }
      if (params.jointApplicant) {
        params = transformDob(['jointApplicant', 'dateOfBirth'], params);
      }

      try {
        // TODO: Missing params include clientVariable4. I have
        // a question out to Tim to confirm whether these are still needed.
        const response = await fetch(PATH, {
          method: 'POST',
          body: {
            ...params,
            ...overrides,
            token: token,
            reCaptchaToken,
            iovationBlackbox: blackbox,
            prequalificationId: prequalificationId,
            location,
            channel,
            subchannel,
            applicationSubType,
            departmentId,
            mockResponse: mockPrequalApp,
          },
        });

        setAnalyticsVariablesByApplication(params);

        if (response.ok) {
          const instantCredit: InstantCredit = await response.json();
          dispatch(mergeInstantCredit(instantCredit));
          const {
            isApproved,
            isDeclined,
            hasExistingAccount,
            callId,
            applicationId,
            accountId,
          } = instantCredit;
          setAnalyticsVariables({
            callId: callId,
            applicationId: applicationId,
            accountId: accountId,
          });
          let result = PrequalApplicationResultType.Approved;
          let message = 'Application successfully approved';

          if (hasExistingAccount) {
            handleExistingAccount(instantCredit, { sendToSdk: false });
          } else {
            dispatch(mergeInstantCredit(instantCredit));
          }

          if (isDeclined) {
            result = PrequalApplicationResultType.Declined;
            message = 'Application is declined';
          } else if (!isApproved) {
            result = PrequalApplicationResultType.Pending;
            message = 'Application is pending';
          } else if (hasExistingAccount) {
            result = PrequalApplicationResultType.AccountExists;
            message = 'An existing account exists';
          }

          fmcDispatch(
            receivePrequalApplicationResult({
              result,
              message,
              callId: callId || null,
              status: response.status,
              rewards: instantCredit.rewards,
              applicationId,
              cardType: instantCredit.cardType,
              mobilePhone: params.mobilePhone || undefined,
              alternativePhone: params.alternativePhone || undefined,
              emailAddress: params.emailAddress || undefined,
            }),
          );

          return Promise.resolve(instantCredit);
        } else {
          // Expect this is to be a 4xx error, in which case
          // the server is expected to return an error object.
          // So we'll attempt to parse it and get at the error message,
          // or just return the entire body if it cannot be parsed.

          const body = await response.text();
          let message = body;
          try {
            message = JSON.parse(message).message;
          } catch (e) {
            /* No need to do anything */
          }

          fmcDispatch(
            receivePrequalApplicationResult({
              result: PrequalApplicationResultType.Error,
              callId: null,
              message,
              status: response.status,
            }),
          );

          setError(true);
          return Promise.reject(new PrequalificationApplicationError());
        }
      } catch (e) {
        if (catchOnly(e, NonClientError)) {
          // Let FmcHost know about server and network errors.
          let status = 0;
          let message = 'NetworkError';

          if (e instanceof ServerError) {
            status = e.response.status;
            message = await e.response.text();
          }

          fmcDispatch(
            receivePrequalApplicationResult({
              result: PrequalApplicationResultType.Error,
              callId: null,
              status: status,
              message: message,
            }),
          );
        }
        // Re-throw so that any exceptions can be handled downstream.
        throw e;
      }
    },
    [
      iovationBlackbox,
      queryStoreNumber,
      defaultStoreNumber,
      fetch,
      token,
      prequalificationId,
      location,
      channel,
      subchannel,
      applicationSubType,
      departmentId,
      mockPrequalApp,
      setAnalyticsVariablesByApplication,
      dispatch,
      setAnalyticsVariables,
      fmcDispatch,
      handleExistingAccount,
    ],
  );

  return [applyForCredit, status, error];
};

export default usePrequalificationApplication;
